import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc, getDoc, getDocs, collection, updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { enablePush, disablePush } from '../firebase-messaging';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import SmsOptInModal from '../components/SmsOptInModal';
import LeakUsageSection from '../components/LeakUsageSection';
import PlusPromptModal from '../components/PlusPromptModal';
import { plusBadge, plusIsOn, freeLowOf, FREE_LOW_OPTIONS } from '../utils/plusStatus';
import SharingPaused from '../components/SharingPaused';
import CollapsibleCard from '../components/CollapsibleCard';
import useSelectedDevice from '../hooks/useSelectedDevice';
import useStallRecovery from '../hooks/useStallRecovery';
import useEmailVerified from '../hooks/useEmailVerified';
import { withRetry, cachedDocs, cachedDoc } from '../utils/firestoreRetry';
import '../styles/Alerts.css';

// Normalize a raw phone number to E.164 (US-centric). Mirrors the
// server-side check in functions/index.js. Returns null if implausible.
function normalizeE164(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  let s;
  if (trimmed.startsWith('+')) {
    s = '+' + trimmed.slice(1).replace(/\D/g, '');
  } else {
    const d = trimmed.replace(/\D/g, '');
    if (d.length === 10) s = '+1' + d;
    else if (d.length === 11 && d[0] === '1') s = '+' + d;
    else s = '+' + d;
  }
  return /^\+[1-9]\d{7,14}$/.test(s) ? s : null;
}

// Where tank alerts come FROM. Display copy only — the real sender lives in
// functions/index.js. Named on screen because the single most common reason
// an alert "doesn't work" is that it landed in spam, and a non-technical user
// can't search for a sender nobody ever told them.
const ALERT_SENDER = 'alerts@sokowatersystems.com';

// SMS opt-in UI. Kept hidden until the Twilio backend (functions + secrets)
// was deployed; enabled at Phase 2 go-live (A2P campaign approved). The
// backend gate (smsEnabled && phoneVerified) applies regardless.
const SMS_UI_ENABLED = true;

// Header icons for the two collapsible alert boxes.
function IconBell() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M9 18a3 3 0 0 0 6 0" />
    </svg>
  );
}
function IconDroplet() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" />
    </svg>
  );
}

// ── Alerts page ──────────────────────────────────────────────────────────
//
// Top-level Alerts screen (moved out of the Settings "Alerts" tab).
// Reads/writes the claim doc alerts block: users/{uid}/devices/{deviceId}.
//   alerts.enabled            — master email alerts
//   alerts.pushEnabled        — push notifications (permission-gated)
//   alerts.offline            — offline / no-report alert
//   alerts.thresholds.low30 / low20 / low10 / full
//   alerts.thresholds.custom  — { on, value }  (NEW single custom % level)
//
// Auto-saves on every change — no Save button. Writes the whole alerts
// object from local state (device pushes never touch this block).

// How long after the last keystroke a custom level is saved.
const CUSTOM_SAVE_DELAY_MS = 800;

// A channel row Free doesn't include: shown off, with a Plus tag, and tapping
// it opens the Plus prompt instead of turning anything on.
function PlusLockedRow({ title, hint, onClick }) {
  return (
    <button type="button" className="alerts-threshold-row plus-locked-row" onClick={onClick}>
      <input type="checkbox" checked={false} readOnly tabIndex={-1} aria-hidden="true" />
      <span>
        <span style={{ display: 'block', fontWeight: 500 }}>
          {title} <span className="leakpro-pill plus-locked-row__pill">SoKo Plus</span>
        </span>
        <span className="hint">{hint}</span>
      </span>
    </button>
  );
}

export default function Alerts({ user }) {
  const navigate = useNavigate();
  // ?focus=away|irrigation — the leak modal links straight to a control in the
  // Leak protection box, which is collapsed by default.
  const [searchParams] = useSearchParams();

  const [devices, setDevices]   = useState([]);
  const [selected, setSelected] = useSelectedDevice(user, devices);
  const [alerts, setAlerts]     = useState(null);   // null = loading
  const [loading, setLoading]   = useState(true);
  const slowLoad = useStallRecovery(loading); // auto-reload once, else show button
  const [saved, setSaved]       = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  // ── Email confirmation, asked for at the moment it starts to matter ──
  //
  // Nagging every new user to confirm their address is noise: most of them
  // never turn email alerts on. But a customer who ticks "email me" with a
  // typo'd address gets silence forever and no way to tell. So the link goes
  // out here, on the switch, where the reason for it is obvious.
  //
  // Deliberately NOT a gate: alerts still send to an unconfirmed address. A
  // tank alert is the whole product, and withholding one because somebody
  // didn't click a link would be the worse failure.
  //
  // emailConfirm: 'idle' | 'sending' | 'sent' | 'error'
  const [emailVerified, recheckEmailVerified] = useEmailVerified(user);
  const [emailConfirm, setEmailConfirm] = useState('idle');
  const [confirmFrom, setConfirmFrom] = useState('');

  // Draft text for the custom-% input. Kept separate from the persisted
  // numeric value so the field can be empty / mid-edit without the value
  // snapping back on every keystroke (the old bug). Firestore always holds
  // a valid number; this holds whatever the user is currently typing.
  const [customDraft, setCustomDraft] = useState('');
  // Custom level save state: 'saved' | 'pending' (typed, waiting for a pause)
  // | 'saving' | 'error'. Drives the chip's check and the status line under
  // the row. customJustSaved keeps "Saved ✓" up for a moment after a save.
  const [customSave, setCustomSave] = useState('saved');
  const [customJustSaved, setCustomJustSaved] = useState(false);
  const [customEditing, setCustomEditing] = useState(false);
  const customInputRef = useRef(null);
  const customTimer = useRef(null);
  const customPending = useRef(null);   // the typed value waiting to be saved
  const flushCustomRef = useRef(() => {});
  // Switching devices drops an unsaved custom value rather than letting it land
  // on the newly selected device.
  useEffect(() => () => {
    window.clearTimeout(customTimer.current);
    customPending.current = null;
    setCustomEditing(false);
  }, [selected]);

  // ── SMS / phone verification (per-user, not per-device) ──
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [savedPhone, setSavedPhone]       = useState('');   // verified number on file (E.164)
  const [phoneInput, setPhoneInput]       = useState('');   // editable field
  const [codeInput, setCodeInput]         = useState('');
  const [verifyStep, setVerifyStep]       = useState('phone'); // 'phone' | 'code' (modal step)
  const [smsModalOpen, setSmsModalOpen]   = useState(false);
  const [smsBusy, setSmsBusy]             = useState(false);
  const [smsError, setSmsError]           = useState('');
  const [smsNotice, setSmsNotice]         = useState('');

  // ── Load the user's phone + verified status (users/{uid}) ──
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await withRetry(() => getDoc(doc(db, 'users', user.uid)));
        const p = snap.exists() ? snap.data() : {};
        setPhoneVerified(p.phoneVerified === true);
        setSavedPhone(p.phoneNumber || '');
        setPhoneInput(p.phoneNumber || '');
      } catch (err) {
        console.error('Alerts: load phone', err);
      }
    })();
  }, [user]);

  // ── Load claimed devices (for the picker) ──────────────
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const claimsCol = collection(db, 'users', user.uid, 'devices');
    const buildList = (claimsSnap, useCache) => Promise.all(claimsSnap.docs.map(async (claimDoc) => {
      const claim = { id: claimDoc.id, ...claimDoc.data() };
      const ref = doc(db, 'devices', claim.deviceId);
      const devSnap = useCache ? await cachedDoc(ref) : await getDoc(ref).catch(() => null);
      const dev = (devSnap && devSnap.exists()) ? devSnap.data() : null;
      return {
        ...claim,
        name: dev ? (dev.name || claim.deviceId) : claim.deviceId,
        // Paid-tier entitlement flag lives on the device doc (same field the
        // cloud leak scan gates on). Absent/false → the free tier.
        leakDetection: dev?.leakDetection === true,
        // Plus state (status, plan, dates, comped). Cloud-written only: the
        // Stripe webhook and the admin comp switch.
        plus: dev?.plus || null,
        // The DEVICE owner's email (the claim's own ownerEmail is the claim
        // holder's) — named in the "sharing is paused" note.
        deviceOwnerEmail: dev?.ownerEmail || null,
        // Second sensor present — same signal the Dashboard renders tank 2 on.
        // Gates the dual-tank-only controls below.
        dualTank: dev?.lastReading?.s2 === true,
      };
    }));
    (async () => {
      // Cache-first paint: if IndexedDB already has this list, show it instantly.
      const cached = await cachedDocs(claimsCol);
      if (cached && !cancelled) { setDevices(await buildList(cached, true)); setLoading(false); }
      // Authoritative server read.
      try {
        const claimsSnap = await withRetry(() => getDocs(claimsCol));
        if (!cancelled) setDevices(await buildList(claimsSnap, false));
      } catch (err) {
        console.error('Alerts: load devices', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── Load alerts for the selected device ────────────────
  useEffect(() => {
    if (!user || !selected) return;
    let cancelled = false;
    const ref = doc(db, 'users', user.uid, 'devices', selected);
    (async () => {
      // Cache-first paint, then authoritative read.
      const cached = await cachedDoc(ref);
      if (cached && !cancelled) setAlerts(cached.data().alerts || {});
      try {
        const snap = await withRetry(() => getDoc(ref));
        if (!cancelled) setAlerts(snap.exists() ? (snap.data().alerts || {}) : {});
      } catch (err) {
        console.error('Alerts: load claim', err);
        if (!cancelled) setAlerts({});
      }
    })();
    return () => { cancelled = true; };
  }, [user, selected]);

  const currentDevice = devices.find((d) => d.deviceId === selected);

  // Dev-only preview switch for the paid Leak & Usage card: `?pro=1` in the URL
  // (sticky in this browser) unlocks the controls on localhost so both the
  // locked and unlocked states can be previewed without touching real
  // entitlement. `?pro=0` clears it. Never affects production behavior — the
  // real gate is the device's `leakDetection` flag.
  const previewForced = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('pro') === '1') { window.localStorage.setItem('soko.previewPro', '1'); return true; }
      if (p.get('pro') === '0') { window.localStorage.removeItem('soko.previewPro'); return false; }
      return window.localStorage.getItem('soko.previewPro') === '1';
    } catch { return false; }
  }, []);
  // Plus turns leak protection on (devices/{id}.leakDetection, set by the
  // cloud from the Stripe subscription or the admin comp switch).
  const leakEntitled = currentDevice?.leakDetection === true || previewForced;

  // ── SoKo Plus ──────────────────────────────────────────
  // Plus lives in Settings → Plus. Here, the locked leak section's "See Plus"
  // opens the shared Plus prompt.
  const [plusPromptOpen, setPlusPromptOpen] = useState(false);
  const [plusPromptReason, setPlusPromptReason] = useState('leak');
  const openPlusPrompt = (reason) => { setPlusPromptReason(reason); setPlusPromptOpen(true); };

  // Checkout used to return here (?plus=…). Send any such link on to
  // Settings → Plus, which shows the result.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('plus');
      if (!v) return;
      const device = params.get('device');
      navigate(`/settings?tab=plus&plus=${encodeURIComponent(v)}${device ? `&device=${encodeURIComponent(device)}` : ''}`,
        { replace: true });
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const a = alerts || {};
  const t = a.thresholds || {};

  // ── Free (no Plus) ─────────────────────────────────────
  // One low-water email at a level they pick (off by default); push, text,
  // other levels, custom, full and offline are Plus. Their saved Plus choices
  // are left untouched (the cloud ignores them on Free) and come back with
  // Plus. A shared user sees why sharing is paused instead.
  const alertsFree = !!currentDevice && !plusIsOn(currentDevice.plus);
  const sharingPaused = alertsFree && currentDevice.role !== 'owner';
  const freeLow = freeLowOf(a);
  const setFreeLow = (key) => persist({ ...a, freeLow: key || null });
  const custom = t.custom || { on: false, value: 25 };

  // Sync the draft to the persisted value when the device changes or the
  // custom section is toggled on. Deliberately NOT keyed on custom.value, so
  // committing a keystroke doesn't re-run this and clobber what's being typed.
  useEffect(() => {
    setCustomDraft(custom.on ? String(custom.value ?? '') : '');
    setCustomSave('saved');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, custom.on]);

  function flashSaved() {
    setSaved(true);
    window.clearTimeout(flashSaved._t);
    flashSaved._t = window.setTimeout(() => setSaved(false), 1600);
  }

  // Merge a patch into the local alerts object and persist the whole block.
  async function persist(next) {
    setAlerts(next);
    if (!selected) return false;
    try {
      await updateDoc(doc(db, 'users', user.uid, 'devices', selected), { alerts: next });
      flashSaved();
      return true;
    } catch (err) {
      console.error('Alerts: save', err);
      return false;
    }
  }

  const setTop = (key, value) => persist({ ...a, [key]: value });

  // Send (or re-send) the confirm-your-address link.
  async function sendConfirmLink() {
    setEmailConfirm('sending');
    try {
      const res = await httpsCallable(functions, 'sendVerificationEmail')();
      if (res?.data?.alreadyVerified) {
        await recheckEmailVerified();
        setEmailConfirm('idle');
        return;
      }
      setConfirmFrom(res?.data?.sentFrom || '');
      setEmailConfirm('sent');
    } catch (err) {
      console.error('Alerts: sendVerificationEmail', err);
      setEmailConfirm('error');
    }
  }

  // Email toggle. Saves the preference first — that's the user's actual
  // intent and it must stick even if the mail send fails — then sends the
  // confirmation link if the address has never been confirmed.
  async function toggleEmail(next) {
    await setTop('enabled', next);
    if (!next) {
      setEmailConfirm('idle');
      return;
    }
    if (emailVerified) return;
    // Already asked this visit — don't mail again just because they
    // toggled off and back on.
    if (emailConfirm === 'sent' || emailConfirm === 'sending') return;
    await sendConfirmLink();
  }
  const setThreshold = (key, value) =>
    persist({ ...a, thresholds: { ...t, [key]: value } });
  const setCustom = (patch) =>
    persist({ ...a, thresholds: { ...t, custom: { ...custom, ...patch } } });
  // A new custom VALUE: shows "saving" until the write lands, then "Saved ✓"
  // briefly. Saved when they stop typing (CUSTOM_SAVE_DELAY_MS), or at once on
  // leaving the box or pressing Enter -- never per keystroke, which used to
  // store a 7% alert on the way to typing 70.
  // Opening the box (Change, or Add) puts the cursor in it with the number selected.
  useEffect(() => {
    if (customEditing && customInputRef.current) customInputRef.current.focus();
  }, [customEditing]);
  const startCustomEdit = () => {
    setCustomDraft(custom.on ? String(custom.value ?? '') : '');
    setCustomSave('saved');
    setCustomEditing(true);
  };

  const saveCustomValue = async (value) => {
    setCustomSave('saving');
    // Turns the level on too: adding one is just saving its first number.
    const ok = await setCustom({ on: true, value });
    setCustomSave(ok ? 'saved' : 'error');
    if (ok) {
      setCustomJustSaved(true);
      window.clearTimeout(saveCustomValue._t);
      saveCustomValue._t = window.setTimeout(() => setCustomJustSaved(false), 2000);
    }
  };
  const cancelCustomSave = () => {
    window.clearTimeout(customTimer.current);
    customPending.current = null;
  };
  const flushCustomSave = () => {
    window.clearTimeout(customTimer.current);
    const raw = customPending.current;
    customPending.current = null;
    if (raw == null || raw === '') return;
    const n = Math.max(0, Math.min(99, parseInt(raw, 10) || 0));
    setCustomDraft(String(n));
    if (!custom.on || n !== custom.value) saveCustomValue(n);
    else setCustomSave('saved');
  };
  const scheduleCustomSave = (raw) => {
    window.clearTimeout(customTimer.current);
    if (raw === '') { customPending.current = null; setCustomSave('pending'); return; }
    customPending.current = raw;
    setCustomSave('pending');
    // Through a ref, so the delayed save runs with THIS moment's settings, not
    // the ones from when typing started (another chip may have changed since).
    customTimer.current = window.setTimeout(() => flushCustomRef.current(), CUSTOM_SAVE_DELAY_MS);
  };
  flushCustomRef.current = flushCustomSave;

  // Push toggle runs the permission flow before persisting.
  async function togglePush(next) {
    setPushBusy(true);
    setPushError('');
    try {
      if (next) {
        const token = await enablePush(user.uid);
        if (!token) {
          setPushError(
            (typeof Notification !== 'undefined' && Notification.permission === 'denied')
              ? 'Notifications are blocked in your browser settings.'
              : "Couldn't turn on notifications. Try again."
          );
          setPushBusy(false);
          return;
        }
      } else {
        await disablePush(user.uid);
      }
      await setTop('pushEnabled', next);
    } catch (err) {
      console.error('Alerts: push toggle', err);
      setPushError('Something went wrong. Please try again.');
    } finally {
      setPushBusy(false);
    }
  }

  // ── Phone verification flow (in the modal) ─────────────
  // A pending verification is derived from Firestore, so it survives the
  // user leaving and coming back: number on file + not yet verified.
  const smsPending = !!savedPhone && phoneVerified !== true;

  function openSmsModal(step) {
    setSmsError('');
    setSmsNotice('');
    setVerifyStep(step);
    if (step === 'phone') setPhoneInput(savedPhone || '');
    if (step === 'code') setCodeInput('');
    setSmsModalOpen(true);
  }

  function closeSmsModal() {
    setSmsModalOpen(false);
    setSmsBusy(false);
    // State (savedPhone / phoneVerified) is left intact so a half-finished
    // verification stays resumable from the "pending" row.
  }

  async function sendCode() {
    setSmsError('');
    setSmsNotice('');
    const e164 = normalizeE164(phoneInput);
    if (!e164) {
      setSmsError('Enter a valid mobile number, e.g. (808) 555-1234.');
      return;
    }
    setSmsBusy(true);
    try {
      await httpsCallable(functions, 'startPhoneVerification')({ phoneNumber: e164 });
      // Server stores the pending number + phoneVerified:false; mirror it.
      setSavedPhone(e164);
      setPhoneVerified(false);
      setVerifyStep('code');
      setCodeInput('');
      setSmsNotice(`We texted a code to ${e164}.`);
    } catch (err) {
      console.error('startPhoneVerification failed:', err);
      setSmsError(err?.message || "Couldn't send the code. Try again.");
    } finally {
      setSmsBusy(false);
    }
  }

  async function verifyCode() {
    setSmsError('');
    setSmsNotice('');
    setSmsBusy(true);
    try {
      const res = await httpsCallable(functions, 'checkPhoneVerification')({ code: codeInput });
      if (res?.data?.ok) {
        setPhoneVerified(true);
        // Verifying with the consent shown IS the opt-in — turn text alerts
        // on for this device and close the modal back to the page.
        await setTop('smsEnabled', true);
        setSmsModalOpen(false);
        setCodeInput('');
      } else {
        setSmsError('That code was incorrect or expired. Request a new one.');
      }
    } catch (err) {
      console.error('checkPhoneVerification failed:', err);
      setSmsError(err?.message || "Couldn't check the code. Request a new one.");
    } finally {
      setSmsBusy(false);
    }
  }

  // Turning on the SMS channel is only allowed for a verified number. If not
  // verified yet, toggling ON opens the opt-in modal instead of flipping the
  // flag (verifyCode() flips smsEnabled once the code checks out). Resume at
  // the code step if a verification is already pending for a number on file.
  async function toggleSms(next) {
    if (next && !phoneVerified) { openSmsModal(smsPending ? 'code' : 'phone'); return; }
    await setTop('smsEnabled', next);
  }

  const LEVELS = [
    { key: 'low30', label: 'Below 30%' },
    { key: 'low20', label: 'Below 20%' },
    { key: 'low10', label: 'Below 10%' },
    { key: 'full',  label: 'Almost full' },
  ];

  // A channel (email or push) must be on before low-water/offline alerts mean
  // anything — with no channel there is nowhere to send them.
  const channelOn = alertsFree
    ? a.enabled === true
    : (a.enabled === true || a.pushEnabled === true || a.smsEnabled === true);
  const anyLowSelected = LEVELS.some((lv) => t[lv.key]) || custom.on === true;
  // Any alert condition at all (low-water level, custom level, or offline).
  const hasAnyAlert = alertsFree ? !!freeLow : (anyLowSelected || a.offline === true);

  // Never leave someone in a silent "channel on but nothing selected" state:
  // when a channel is turned on and nothing is chosen yet, auto-set a sensible
  // starting kit — a low level (20%) AND the offline alert — in ONE write.
  // Keyed on channelOn so it fires only on the off->on transition; if the user
  // later clears every level (channelOn stays true) we do NOT re-add it, so the
  // "pick a level" warning can show.
  useEffect(() => {
    // Free: nothing is switched on for them (they pick their one level).
    if (alertsFree) return;
    if (!channelOn) return;
    if (hasAnyAlert) return;
    persist({ ...a, offline: true, thresholds: { ...t, low20: true } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelOn]);

  // The "When should we alert you?" block (low-water + offline) is collapsible,
  // matching the Leak & Usage card, so the page stays short. Collapsed by
  // default; auto-opens only when a channel is on but no alert is chosen yet
  // (so the "pick an alert" nudge and the controls are visible together).
  const [optionsOpen, setOptionsOpen] = useState(false);
  useEffect(() => {
    if (channelOn && !hasAnyAlert) setOptionsOpen(true);
  }, [channelOn, hasAnyAlert]);

  // Jump the user straight to the alert-level chips from the top-of-page
  // "you haven't picked a level" warning and the "more levels" hint. Opening
  // the collapsible first, then scrolling once its content is in the DOM.
  const levelsRef = useRef(null);
  const scrollToLevels = () => {
    setOptionsOpen(true);
    // Align the Water level box to the TOP of the viewport (not centered) so
    // its options — and whatever sits below — are all visible. scroll-margin-top
    // on .collapse-head clears the sticky top bar.
    setTimeout(() => levelsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  // One-line summary of the water-level alerts. A single level shows its
  // label; two or more collapse to a count so the line stays short.
  const optionsSummary = alertsFree
    ? (freeLow ? `Email below ${freeLow.value}%` : 'No low-water alert set')
    : (() => {
    const levelCount = LEVELS.filter((lv) => t[lv.key]).length + (custom.on ? 1 : 0);
    const parts = [];
    if (levelCount >= 2) parts.push(`${levelCount} tank-level alerts`);
    else if (levelCount === 1) {
      const one = LEVELS.find((lv) => t[lv.key]);
      parts.push(one ? one.label : `Below ${custom.value}%`);
    }
    // The offline alert lives in this card now, so the collapsed line has to
    // show it — otherwise someone who has only that one on reads "None chosen
    // yet" and turns something else on they don't need.
    if (a.offline === true) parts.push('Offline alert');
    return parts.length ? parts.join(' · ') : 'None chosen yet';
  })();

  // Notification-methods box: collapsed by default, but auto-opened once per
  // device when its settings load with NO channel on — so a new/unconfigured
  // device never hides the critical first step. After that, manual toggle wins.
  const [channelsOpen, setChannelsOpen] = useState(false);
  const channelInitRef = useRef(null);
  useEffect(() => {
    if (alerts === null) return;                       // not loaded yet
    if (channelInitRef.current === selected) return;   // already initialized this device
    channelInitRef.current = selected;
    setChannelsOpen(!channelOn);
  }, [alerts, selected, channelOn]);

  // Show all three channels always; the ones that are off read struck-through
  // (italic) rather than being dropped, so you can see what's available.
  const chanTag = (label, on) => <span className={on ? '' : 'chan-off'}>{label}</span>;
  const channelsSummary = alertsFree ? (
    <>
      {chanTag('Email', a.enabled === true)}
      {' · Push and text with SoKo Plus'}
    </>
  ) : (
    <>
      {chanTag('Email', a.enabled === true)}
      {' · '}
      {chanTag('Push', a.pushEnabled === true)}
      {' · '}
      {chanTag('Text', a.smsEnabled === true)}
    </>
  );

  if (loading) {
    return (
      <div className="app-container">
        <TopBar subtitle="Alerts" />
        <div className="loading-page">
          <div className="spinner" />
          {slowLoad && (
            <button type="button" className="btn btn-primary" style={{ marginTop: 16 }}
              onClick={() => window.location.reload()}>
              Reload
            </button>
          )}
        </div>
        <BottomNav />
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="app-container">
        <TopBar subtitle="Alerts" />
        <div className="page-content">
          <div className="empty-state">
            <h3>No devices yet</h3>
            <p>Add a device to set up alerts.</p>
            <button type="button" className="btn btn-primary" style={{ width: 'auto', padding: '10px 24px' }}
              onClick={() => navigate('/devices')}>+ Add a device</button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="app-container">
      <TopBar
        subtitle={currentDevice?.name || 'Alerts'}
        rightContent={saved ? <span className="alerts-saved">Saved ✓</span> : null}
      />

      <div className="page-content">

        {/* Monitor pills, with this monitor's Plus status beside them. The
            chip opens Settings → Plus (or the Plus prompt when it has none). */}
        <div className="alerts-topline">
          {devices.length > 1 && (
            <div className="device-selector">
              {devices.map((d) => (
                <button
                  key={d.deviceId}
                  type="button"
                  className={`device-pill ${selected === d.deviceId ? 'active' : ''}`}
                  onClick={() => { setSelected(d.deviceId); setAlerts(null); }}
                >
                  {d.name}
                </button>
              ))}
            </div>
          )}
          {currentDevice && (() => {
            const badge = plusBadge(currentDevice.plus);
            return (
              <button type="button" className={`plus-chip plus-chip--${badge.tone}`}
                onClick={() => (badge.tone === 'off' ? openPlusPrompt(null) : navigate('/settings?tab=plus'))}>
                {badge.tone === 'on' && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
                )}
                {badge.label}
              </button>
            );
          })()}
        </div>

        {sharingPaused ? (
          <SharingPaused ownerEmail={currentDevice?.deviceOwnerEmail} />
        ) : alerts === null ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Top-of-page warning: alerts are on but nothing is chosen, so
                nothing would fire. Prominent, first thing seen, with a button
                (stacked below the text) that jumps to the alert options. */}
            {channelOn && !hasAnyAlert && (
              <div className="alerts-warn-top" role="alert">
                <div className="alerts-warn-top__row">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a1 1 0 0 0 .86 1.5h18.64a1 1 0 0 0 .86-1.5L13.71 3.86a1 1 0 0 0-1.72 0z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                  <span>You've turned on alerts, but haven't chosen which ones to receive yet.</span>
                </div>
                <button type="button" className="alerts-warn-top__btn" onClick={scrollToLevels}>Choose an alert</button>
              </div>
            )}

            {/* Sharing hint — owners only. Shared users can't manage access,
                so it would just be noise for them. */}
            {currentDevice?.role === 'owner' && (
              <div className="alert-banner alert-banner--info" style={{ marginBottom: 16 }}>
                <span className="alert-banner__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></svg>
                </span>
                <div>
                  <div className="alert-banner-desc">
                    Want others to get alerts too?{' '}
                    <button type="button" className="linklike" onClick={() => navigate('/settings?tab=sharing')}>
                      Share access →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Box 1 — notification methods (email / push / text). */}
            <CollapsibleCard
              icon={<IconBell />}
              title="Notification methods"
              summary={channelsSummary}
              open={channelsOpen}
              onToggle={() => setChannelsOpen((v) => !v)}
            >
              <div className="alerts-thresholds" style={{ marginTop: 0, paddingTop: 0, borderTop: 0 }}>
                <label className={`alerts-threshold-row ${emailConfirm === 'sending' ? 'is-busy' : ''}`}>
                  <input type="checkbox" checked={a.enabled === true}
                    disabled={emailConfirm === 'sending'}
                    onChange={(e) => toggleEmail(e.target.checked)} />
                  <span>
                    <span style={{ display: 'block', fontWeight: 500 }}>
                      Send me email alerts
                      {emailConfirm === 'sending' && <span className="inline-spinner" aria-hidden="true" />}
                    </span>
                    <span className="hint">
                      Sent to {user?.email || 'your inbox'} from <strong>{ALERT_SENDER}</strong>.
                    </span>
                  </span>
                </label>

                {/* Confirm-your-address prompt. Only appears once email
                    alerts are actually on and the address is unconfirmed, so
                    it reads as the next step rather than a scolding. */}
                {a.enabled === true && !emailVerified && (
                  <div className="alerts-confirm-email">
                    {emailConfirm === 'sent' ? (
                      <>
                        <div className="alerts-confirm-email__head">
                          Check your email to confirm this address
                        </div>
                        <div className="hint" style={{ margin: 0 }}>
                          We sent a link to <strong>{user?.email}</strong>
                          {confirmFrom ? <> from <strong>{confirmFrom}</strong></> : null}.
                          Open it so we know your alerts are reaching you. Nothing
                          there after a few minutes? Look in your spam or junk folder.
                        </div>
                        <div className="alerts-confirm-email__actions">
                          <button type="button" className="linklike" onClick={sendConfirmLink}>
                            Send it again
                          </button>
                          <button type="button" className="linklike"
                            onClick={() => navigate('/alerts?account=email')}>
                            Wrong address? Change it
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="hint" style={{ margin: 0 }}>
                          {emailConfirm === 'error'
                            ? "We couldn't send the confirmation link just now."
                            : <>Confirm <strong>{user?.email}</strong> so you know your alerts are getting through.</>}
                        </div>
                        <div className="alerts-confirm-email__actions">
                          <button type="button" className="linklike"
                            onClick={sendConfirmLink}
                            disabled={emailConfirm === 'sending'}>
                            {emailConfirm === 'sending'
                              ? 'Sending…'
                              : (emailConfirm === 'error' ? 'Try again' : 'Send me the link')}
                          </button>
                          <button type="button" className="linklike"
                            onClick={() => navigate('/alerts?account=email')}>
                            Wrong address? Change it
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Turning push ON asks the browser for permission and
                    registers a token with FCM -- a second or two during which
                    nothing visibly happens. Watching a new customer, they
                    pressed it repeatedly assuming it was broken. The spinner
                    and the changed label say "working", not "frozen". */}
                {alertsFree ? (
                  <PlusLockedRow title="Push notifications" hint="A banner on this phone, even when closed."
                    onClick={() => openPlusPrompt('alerts')} />
                ) : (
                <label className={`alerts-threshold-row ${pushBusy ? 'is-busy' : ''}`}>
                  <input type="checkbox" checked={a.pushEnabled === true} disabled={pushBusy}
                    onChange={(e) => togglePush(e.target.checked)} />
                  <span>
                    <span style={{ display: 'block', fontWeight: 500 }}>
                      Enable push notifications
                      {pushBusy && <span className="inline-spinner" aria-hidden="true" />}
                    </span>
                    <span className="hint">
                      {pushBusy
                        ? (a.pushEnabled === true ? 'Turning off…' : 'Setting up — this can take a few seconds…')
                        : 'A banner on this phone, even when closed.'}
                    </span>
                  </span>
                  {/* Announced to screen readers, which get no spinner. */}
                  <span className="sr-only" role="status" aria-live="polite">
                    {pushBusy ? 'Setting up push notifications, please wait' : ''}
                  </span>
                </label>
                )}

                {SMS_UI_ENABLED && alertsFree && (
                  <PlusLockedRow title="Text alerts" hint="A text when a tank needs attention."
                    onClick={() => openPlusPrompt('alerts')} />
                )}
                {SMS_UI_ENABLED && !alertsFree && (
                <label className="alerts-threshold-row">
                  <input
                    type="checkbox"
                    checked={a.smsEnabled === true}
                    disabled={smsBusy}
                    onChange={(e) => toggleSms(e.target.checked)}
                  />
                  <span>
                    <span style={{ display: 'block', fontWeight: 500 }}>Send me text alerts</span>
                    {phoneVerified ? (
                      <span className="hint">
                        Texts to {savedPhone}. Msg &amp; data rates may apply.{' '}
                        <button type="button" className="linklike" onClick={() => openSmsModal('phone')}>Change number</button>
                      </span>
                    ) : smsPending ? (
                      <span className="hint">
                        Verifying {savedPhone} —{' '}
                        <button type="button" className="linklike" onClick={() => openSmsModal('code')}>enter code</button>
                        {' · '}
                        <button type="button" className="linklike" onClick={() => openSmsModal('phone')}>change</button>
                      </span>
                    ) : (
                      <span className="hint">
                        Get a text when a tank needs attention. Turn on to verify your number.
                      </span>
                    )}
                  </span>
                </label>
                )}

              </div>
              {pushError && <div className="hint" style={{ color: 'var(--color-copper)', marginTop: 8 }}>{pushError}</div>}
            </CollapsibleCard>

            {/* Box 2 — water level alerts (low-water levels). */}
            <CollapsibleCard
              icon={<IconDroplet />}
              title="Tank alerts"
              summary={optionsSummary}
              open={optionsOpen}
              onToggle={() => setOptionsOpen((v) => !v)}
              headRef={levelsRef}
            >
              {alertsFree ? (
                <div className="alerts-section free-low">
                  <div className="alerts-subhead">Low-water alerts</div>
                  <div className="hint">The Free plan includes one low-water alert.</div>
                  <div className="alert-level-chips" role="radiogroup" aria-label="Low-water email level">
                    {[{ key: null, label: 'Off' }, ...FREE_LOW_OPTIONS.map((o) => ({ key: o.key, label: `Below ${o.value}%` }))].map((o) => {
                      const on = (freeLow ? freeLow.key : null) === o.key;
                      return (
                        <button key={o.key || 'off'} type="button" role="radio" aria-checked={on}
                          className={`alert-level-chip ${on ? 'on' : ''}`}
                          onClick={() => setFreeLow(o.key)}>
                          <span className="alert-level-chip__mark" aria-hidden="true">{on ? '✓' : ''}</span>
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                  {!freeLow && <div className="free-low__none">No low-water alert set.</div>}
                  {freeLow && !a.enabled && (
                    <div className="hint" style={{ color: 'var(--color-copper)' }}>
                      Turn on email under Notification methods to get this.
                    </div>
                  )}
                  <div className="free-low__plus">
                    <div className="alerts-subhead">
                      Want more alerts?{' '}
                      <button type="button" className="linklike free-low__link" onClick={() => openPlusPrompt('alerts')}>Upgrade to SoKo Plus</button>
                    </div>
                    <ul className="free-low__list">
                      <li>Text alerts</li>
                      <li>Push notifications</li>
                      <li>Up to 4 low-water alerts, including your own custom level</li>
                      <li>Almost-full alert</li>
                      <li>Offline alert if the monitor stops reporting</li>
                      <li>Usage and leak alerts to catch leaks and heavy use</li>
                      <li>Monthly usage summary email</li>
                    </ul>
                  </div>
                </div>
              ) : (
              <>
              {!channelOn && (
                <div className="alerts-gate-note">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
                  <span>First turn on a way to reach you above.</span>
                </div>
              )}

              <fieldset className={`alerts-gated-group ${!channelOn ? 'is-disabled' : ''}`} disabled={!channelOn}>
                <div className="alerts-section">
                  <div className="alerts-subhead">Level alerts</div>
                  <div className="hint">Pick the levels you want to be notified about.</div>
                  <div className="alert-level-chips">
                    {LEVELS.map((lv) => (
                      <button
                        key={lv.key}
                        type="button"
                        className={`alert-level-chip ${t[lv.key] ? 'on' : ''}`}
                        onClick={() => setThreshold(lv.key, !t[lv.key])}
                        aria-pressed={t[lv.key] ? 'true' : 'false'}
                      >
                        <span className="alert-level-chip__mark" aria-hidden="true">{t[lv.key] ? '✓' : ''}</span>
                        {lv.label}
                      </button>
                    ))}
                  </div>

                  {/* Custom level: a small header, then the saved level as a
                      chip like the ones above with Change / Remove, or the box
                      while editing. Saves on its own when they stop typing. */}
                  <div className="alert-custom">
                    <div className="alerts-thresholds__title">Custom level</div>
                    {customEditing ? (
                      <div className="alert-custom__edit">
                        <label htmlFor="custom-pct" className="hint">Alert me below</label>
                        <input
                          id="custom-pct"
                          ref={customInputRef}
                          className="alerts-recipient-input"
                          style={{ maxWidth: 90 }}
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          value={customDraft}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw !== '' && !/^\d{1,2}$/.test(raw)) return;   // digits only, 0-99
                            setCustomDraft(raw);
                            scheduleCustomSave(raw);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') { cancelCustomSave(); setCustomEditing(false); }
                          }}
                          onBlur={() => { flushCustomSave(); setCustomEditing(false); }}
                        />
                        <span className="hint">%</span>
                      </div>
                    ) : custom.on ? (
                      <div className="alert-custom__show">
                        <span className="alert-level-chip on alert-level-chip--static">
                          <span className="alert-level-chip__mark" aria-hidden="true">✓</span>
                          Below {custom.value}%
                        </span>
                        <button type="button" className="linklike" onClick={startCustomEdit}>Change</button>
                        <button type="button" className="linklike alert-custom__remove"
                          onClick={() => { cancelCustomSave(); setCustom({ on: false }); }}>Remove</button>
                      </div>
                    ) : (
                      <button type="button" className="alerts-recipient-add" onClick={startCustomEdit}>
                        + Add a custom level
                      </button>
                    )}
                    {(customSave === 'saving' || customSave === 'error' || customJustSaved) && (
                      <div className={`alert-custom-status ${customSave === 'error' ? 'is-error' : ''}`} role="status">
                        {customSave === 'saving' ? 'Saving…'
                          : customSave === 'error' ? 'Didn\u2019t save. Check your connection and try again.'
                          : 'Saved ✓'}
                      </div>
                    )}
                    {custom.on && custom.value === 0 && !customEditing && (
                      <div className="hint" style={{ marginTop: 6 }}>
                        0% alerts you when the tank reads empty.
                      </div>
                    )}
                  </div>
                </div>

                {/* Same full-bleed divider the Plus card uses between its
                    sections — .alerts-section carries no spacing of its own,
                    so without it "Device" sits flush against the level alerts
                    and reads as part of them. */}
                <div className="prefs-divider" style={{ margin: '14px -14px' }} />

                {/* Device health. Inside the gated fieldset on purpose: this
                    used to sit in its own box below everything, outside the
                    gate, so it could be switched on with no email, push or
                    text set up — and the server quite correctly sent nothing.
                    Ticking a box and being covered by nothing is the failure
                    this gate exists to prevent, and it applied to every other
                    alert on the page but this one. */}
                <div className="alerts-section">
                  <div className="alerts-subhead alerts-subhead--section">Device</div>
                  <label className="alerts-threshold-row">
                    <input type="checkbox" checked={a.offline === true}
                      onChange={(e) => setTop('offline', e.target.checked)} />
                    <span>
                      <span style={{ display: 'block', fontWeight: 500 }}>Offline alert</span>
                      <span className="hint">We'll notify you if your device stops reporting for over 12 hours.</span>
                    </span>
                  </label>
                </div>
              </fieldset>
              </>
              )}
            </CollapsibleCard>

            <LeakUsageSection
              focus={searchParams.get('focus')}
              alerts={a}
              persist={persist}
              entitled={leakEntitled}
              previewForced={previewForced && !(currentDevice?.leakDetection === true)}
              onEnable={() => openPlusPrompt('leak')}
              dualTank={currentDevice?.dualTank === true}
            />

            <p className="alerts-autosave-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
              Changes save automatically.
            </p>

            <PlusPromptModal
              open={plusPromptOpen}
              onClose={() => setPlusPromptOpen(false)}
              deviceId={selected}
              plus={currentDevice?.plus}
              isOwner={currentDevice?.role === 'owner'}
              reason={plusPromptReason}
            />

            <SmsOptInModal
              open={smsModalOpen}
              step={verifyStep}
              savedPhone={savedPhone}
              phoneInput={phoneInput}
              setPhoneInput={setPhoneInput}
              codeInput={codeInput}
              setCodeInput={setCodeInput}
              busy={smsBusy}
              error={smsError}
              notice={smsNotice}
              onSendCode={sendCode}
              onVerify={verifyCode}
              onResend={sendCode}
              onClose={closeSmsModal}
            />
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
